import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AxiosError } from "axios";
import { DateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { enUS } from "date-fns/locale/en-US";
import { DataDialog } from "@app/components/atoms";
import CustomImageViewer from "@app/components/atoms/CustomImageViewer/CustomImageViewer";
import {
  getCustomInspectionFormTemplates,
  getInspectionForms,
  getLatestInspection,
} from "@app/api";
import { useAppContext } from "@app/AppContext";
import {
  CurrentStatusPostEnum,
  INVALID_URL,
  ItemCodeEnum,
  ItemResultType,
  ResultType,
} from "@app/constants";
import RecordRTC from "recordrtc";
import { submitCreateInspection } from "./createInspectionSubmit.helper";
import {
  buildItemDraftBaseline,
  submitUpdateInspection,
  type ItemDraftBaseline,
} from "./createInspectionUpdate.helper";
import {
  InspectionNonNumericItemRow,
  InspectionNumericItemRow,
} from "./CreateInspectionItemRows";
import { PreviousInspectionItemDialog } from "./PreviousInspectionItemDialog";
import type {
  InspectionFormDetailResponse,
  InspectionFormListItem,
  ItemDraft,
  LatestInspectionData,
  LatestInspectionItem,
  TemplateInspectionItem,
  TemplateInspectionItemMedia,
} from "./createInspectionForm.types";
import {
  INSPECTION_AT_MIN_DATE,
  INSPECTION_MEDIA_TOTAL_LIMIT,
  buildMediaDraftUpdate,
  capMediaFilesForItem,
  getNumericMax,
  hasPredefinedOptions,
  isIconResultItem,
  isInspectionAtInValidRange,
  isNumericValueValid,
  sortByPosition,
} from "./createInspectionForm.utils";
import {
  setIsLoading,
  setIsUpdatedMachineReportResponses,
  setShowAlert,
} from "@app/store";
import "@app/pages/Features/MachineDetail/Components/InspectionHistory/recording.css";
import { SignatureDialog } from "../SignatureDialog";
import { getGroupIsSignatureRequired } from "../groupSignature.helper";

function isOdoOrSmrItem(
  item?: {
    itemCode?: string | null;
    resultType?: string;
  } | null,
): boolean {
  if (!item) return false;
  return (
    item.itemCode === ItemCodeEnum.ODOMETER ||
    item.itemCode === ItemCodeEnum.SERVICE_METER ||
    item.resultType === ResultType.ODOMETER
  );
}

function hasLatestItemPreviousData(
  item?: LatestInspectionItem | null,
  templateItem?: TemplateInspectionItem | null,
): boolean {
  if (!item) return false;
  if (isOdoOrSmrItem(templateItem) || isOdoOrSmrItem(item)) return false;

  const hasComment = !!item.machineReport?.reportComment?.trim();
  const hasFreeText = !!item.freeText?.trim();
  const hasMedia = (item.machineReport?.machineReportMedias?.length ?? 0) > 0;
  const hasRecording =
    (item.machineReport?.machineReportRecordings?.length ?? 0) > 0;
  const hasResult = !!item.result?.toString().trim();
  return hasResult || hasComment || hasFreeText || hasMedia || hasRecording;
}

function isLatestItemPreviousAbnormal(
  item?: LatestInspectionItem | null,
): boolean {
  return item?.result === ItemResultType.ANOMARY;
}

function isLikelyVideoPath(url: string): boolean {
  const path = url.split("?")[0].toLowerCase();
  return /\.(mp4|mov|webm|m4v|avi)$/i.test(path);
}

function isTemplateMediaVideo(media: TemplateInspectionItemMedia): boolean {
  const mime = (media.mimeType || "").toLowerCase();
  if (mime.includes("video")) {
    return true;
  }
  return (
    isLikelyVideoPath(media.mediaUrl || "") ||
    isLikelyVideoPath(media.fileName || "")
  );
}

function getTemplateMediaPreviewUrl(
  media: TemplateInspectionItemMedia,
): string | undefined {
  const url = media.mediaUrl?.trim();
  if (!url || url === INVALID_URL) {
    return undefined;
  }
  return url;
}

export type {
  InspectionFormDetailResponse,
  InspectionFormListItem,
  ItemDraft,
  TemplateInspectionItem,
  TemplateInspectionItemMedia,
  TemplateInspectionItemOption,
} from "./createInspectionForm.types";

function openImageInViewer(
  url: string,
  setImage: (s: string) => void,
  setOpen: (v: boolean) => void,
): void {
  if (!url) return;
  if (isLikelyVideoPath(url)) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  if (url.startsWith("blob:")) {
    const img = new Image();
    img.onload = () => {
      setImage(url);
      setOpen(true);
    };
    img.onerror = () => {
      window.open(url, "_blank", "noopener,noreferrer");
    };
    img.src = url;
    return;
  }
  setImage(url);
  setOpen(true);
}

function scrollNearestScrollableAncestor(el: HTMLElement | null): void {
  if (!el) return;
  const amount = (el.getBoundingClientRect().height || 150) + 40;
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const canScrollY = /(auto|scroll)/.test(style.overflowY);
    if (canScrollY && node.scrollHeight > node.clientHeight) {
      node.scrollBy({ top: amount, behavior: "smooth" });
      return;
    }
    node = node.parentElement;
  }
}

function createEmptyDraft(_item: TemplateInspectionItem): ItemDraft {
  return {
    resultValue: "",
    numericValue: "",
    numericTouched: false,
    comment: "",
    selectedFreeText: "",
    commentExpanded: false,
    commentError: false,
    mediaFiles: [],
    fileCount: 0,
    hasUploadError: false,
    recordedBlob: null,
    removedMediaUrls: [],
    existingRecordingUrl: null,
    inspectionResultId: null,
  };
}

function mapExistingInspectionToFormDetail(
  data: LatestInspectionData,
): InspectionFormDetailResponse {
  return {
    inspectionFormId: data.inspectionFormId,
    name: data.name || "",
    type: data.type,
    inspectionItems: sortByPosition(data.inspectionItems || []).map((item) => ({
      inspectionItemId: item.inspectionItemId,
      name: item.name,
      description: item.description || "",
      itemCode: item.itemCode ?? null,
      resultType: item.resultType,
      isRequired: item.isRequired ?? false,
      position: item.position ?? 0,
      inspectionItemMedias: item.inspectionItemMedias || [],
      options: item.options ? sortByPosition(item.options) : undefined,
      isUpdated: item.isUpdated === true,
    })),
  };
}

function buildItemDraftsFromExistingInspection(
  items: LatestInspectionItem[],
): Record<string, ItemDraft> {
  const sorted = sortByPosition(items);
  const next: Record<string, ItemDraft> = {};

  for (const item of sorted) {
    const isNumeric =
      item.resultType === ResultType.NUMERIC ||
      item.resultType === ResultType.ODOMETER;
    const isIcon = isIconResultItem(item as TemplateInspectionItem);
    const report = item.machineReport;
    const medias =
      report?.machineReportMedias?.map((media) => ({
        fileName: media.fileName || "",
        mediaUrl: media.mediaUrl || "",
        mimeType: media.mimeType || "",
        captureMediaAt: media.captureMediaAt,
        filePath: media.filePath || "",
        thumbnailUrl: media.thumbnailUrl || "",
      })) || [];
    const recording = report?.machineReportRecordings?.[0];
    const comment = report?.reportComment || "";
    const resultValue = isIcon ? item.result || "" : "";
    const isPredefinedFreeText =
      item.resultType === ResultType.FREE_TEXT &&
      hasPredefinedOptions(item as TemplateInspectionItem);

    next[item.inspectionItemId] = {
      ...createEmptyDraft(item as TemplateInspectionItem),
      resultValue,
      numericValue: isNumeric ? String(item.result ?? "") : "",
      comment,
      selectedFreeText: item.freeText || "",
      commentExpanded:
        resultValue === ItemResultType.ANOMARY || isPredefinedFreeText,
      mediaFiles: medias as unknown as File[],
      fileCount: medias.length,
      existingRecordingUrl: recording?.recordingUrl || null,
      inspectionResultId: item.inspectionResultId || null,
    };
  }

  return next;
}

function buildItemDraftsFromTemplate(
  items: TemplateInspectionItem[],
): Record<string, ItemDraft> {
  const sorted = sortByPosition(items);

  const next: Record<string, ItemDraft> = {};
  for (const item of sorted) {
    const isPredefinedFreeText =
      item.resultType === ResultType.FREE_TEXT && hasPredefinedOptions(item);
    next[item.inspectionItemId] = {
      ...createEmptyDraft(item),
      commentExpanded: isPredefinedFreeText,
    };
  }
  return next;
}

function isInspectionItemInvalid(
  item: TemplateInspectionItem,
  draft: ItemDraft | undefined,
): boolean {
  if (!draft) return true;

  const isNumeric =
    item.resultType === ResultType.NUMERIC ||
    item.resultType === ResultType.ODOMETER;

  if (isNumeric) {
    const numericVal = draft.numericValue.trim();
    if (item.isRequired && !numericVal) return true;
    if (numericVal && !isNumericValueValid(numericVal, getNumericMax(item))) {
      return true;
    }
    return false;
  }

  if (draft.hasUploadError || draft.commentError) return true;

  if (item.resultType === ResultType.FREE_TEXT) {
    return !draft.selectedFreeText.trim();
  }

  const hasResultIcons = isIconResultItem(item);

  if (hasResultIcons && item.isRequired && !draft.resultValue) {
    return true;
  }

  if (hasResultIcons && draft.resultValue === ItemResultType.ANOMARY) {
    const hasComment = draft.comment.trim().length > 0;
    const hasFiles = draft.mediaFiles.length > 0;
    const hasRecording = !!draft.recordedBlob;
    if (!hasComment && !hasFiles && !hasRecording) return true;
  }

  if (
    (draft.resultValue === ItemResultType.OK ||
      draft.resultValue === ItemResultType.NOT_APPLICABLE) &&
    draft.comment !== "" &&
    !draft.comment.trim()
  ) {
    return true;
  }

  return false;
}

function needsCommentExpansionOnSend(
  item: TemplateInspectionItem,
  draft: ItemDraft,
): boolean {
  if (item.resultType === ResultType.FREE_TEXT) {
    return hasPredefinedOptions(item) && !draft.selectedFreeText.trim();
  }

  if (isIconResultItem(item) && draft.resultValue === ItemResultType.ANOMARY) {
    const hasEvidence =
      draft.comment.trim().length > 0 ||
      draft.mediaFiles.length > 0 ||
      !!draft.recordedBlob ||
      !!draft.existingRecordingUrl;
    return !hasEvidence;
  }

  return false;
}

function findItemsNeedingCommentExpansion(
  items: TemplateInspectionItem[],
  itemDrafts: Record<string, ItemDraft>,
): string[] {
  return items
    .filter((item) => {
      const draft = itemDrafts[item.inspectionItemId];
      return draft && needsCommentExpansionOnSend(item, draft);
    })
    .map((item) => item.inspectionItemId);
}

function hasInspectionFormValidationErrors(
  items: TemplateInspectionItem[],
  itemDrafts: Record<string, ItemDraft>,
): boolean {
  return items.some((item) =>
    isInspectionItemInvalid(item, itemDrafts[item.inspectionItemId]),
  );
}

function findFirstInvalidInspectionItemId(
  items: TemplateInspectionItem[],
  itemDrafts: Record<string, ItemDraft>,
): string | null {
  const invalidItem = items.find((item) =>
    isInspectionItemInvalid(item, itemDrafts[item.inspectionItemId]),
  );
  return invalidItem?.inspectionItemId ?? null;
}

function findAllInvalidInspectionItemIds(
  items: TemplateInspectionItem[],
  itemDrafts: Record<string, ItemDraft>,
): Set<string> {
  return new Set(
    items
      .filter((item) =>
        isInspectionItemInvalid(item, itemDrafts[item.inspectionItemId]),
      )
      .map((item) => item.inspectionItemId),
  );
}

function applyOkSelectionToDrafts(
  drafts: Record<string, ItemDraft>,
  items: TemplateInspectionItem[],
  selectOk: boolean,
): Record<string, ItemDraft> {
  const next = { ...drafts };
  for (const item of items) {
    if (!isIconResultItem(item)) continue;
    const id = item.inspectionItemId;
    if (!next[id]) continue;
    next[id] = {
      ...next[id],
      resultValue: selectOk ? ItemResultType.OK : "",
    };
  }
  return next;
}

function cloneBlobForPlayback(blob: Blob): Blob {
  const type =
    blob.type && blob.type !== "application/octet-stream"
      ? blob.type
      : "audio/wav";
  if (blob.type === type) return blob;
  return new Blob([blob], { type });
}

type CreateInspectionPrefill = {
  mode?: "create" | "update";
  inspectionId?: string;
  customInspectionFormId: string;
  inspectionAt: string;
  inspectionDetail: InspectionFormDetailResponse;
  latestInspection?: LatestInspectionData | null;
  existingInspection?: LatestInspectionData | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  groupId: string;
  machineId: string;
  prefill?: CreateInspectionPrefill | null;
  onSuccess?: () => void;
};

export function CreateInspections({
  open,
  onClose,
  groupId,
  machineId,
  prefill = null,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { userTimeFormat } = useAppContext();
  const [selectDialogOpen, setSelectDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "update">("create");
  const [editingInspectionId, setEditingInspectionId] = useState<
    string | undefined
  >(undefined);
  const pendingInitialDraftsRef = useRef<Record<string, ItemDraft> | null>(
    null,
  );
  const originalItemBaselinesRef = useRef<Record<string, ItemDraftBaseline>>(
    {},
  );
  const existingInspectionRef = useRef<LatestInspectionData | null>(null);

  const [forms, setForms] = useState<InspectionFormListItem[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedFormId, setSelectedFormId] = useState("");

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [inspectionDetail, setInspectionDetail] =
    useState<InspectionFormDetailResponse | null>(null);

  const [itemDrafts, setItemDrafts] = useState<Record<string, ItemDraft>>({});
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const inspectionAtFieldRef = useRef<HTMLDivElement | null>(null);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const [openFormNoMic, setOpenFormNoMic] = useState(false);
  const [openFormDeleteRecording, setOpenFormDeleteRecording] = useState(false);
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [isSubmittingInspection, setIsSubmittingInspection] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [flaggedInvalidItemIds, setFlaggedInvalidItemIds] = useState<
    Set<string>
  >(new Set());
  const [pendingExpansionQueue, setPendingExpansionQueue] = useState<string[]>(
    [],
  );
  const submitInFlightRef = useRef(false);

  const markValidationHints = useCallback(() => {
    setValidationAttempted(true);
  }, []);

  const [isRecording, setIsRecording] = useState(false);
  const [recorder, setRecorder] = useState<any>(null);
  const recordingStartTimeRef = useRef<number | null>(null);
  const recordingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [mediaAvailable, setMediaAvailable] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const stoppedRef = useRef(false);
  const recordingTargetItemIdRef = useRef<string | null>(null);
  const [recordingForItemId, setRecordingForItemId] = useState<string | null>(
    null,
  );
  const pendingDeleteRecordingItemIdRef = useRef<string | null>(null);
  const skipNextRecordingBlobRef = useRef(false);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<ReturnType<typeof RecordRTC> | null>(null);

  const [mediaPreviewNonce, setMediaPreviewNonce] = useState(0);
  const [latestInspection, setLatestInspection] =
    useState<LatestInspectionData | null>(null);
  const [latestInspectionPrefilled, setLatestInspectionPrefilled] =
    useState(false);
  const [inspectionAt, setInspectionAt] = useState<string | undefined>(
    undefined,
  );
  const [inspectionAtTouched, setInspectionAtTouched] = useState(false);
  const [previousItemDialogOpen, setPreviousItemDialogOpen] = useState(false);
  const [selectedPreviousItemId, setSelectedPreviousItemId] = useState<
    string | null
  >(null);

  useEffect(() => {
    mediaStreamRef.current = mediaStream;
  }, [mediaStream]);

  useEffect(() => {
    recorderRef.current = recorder;
  }, [recorder]);

  const releaseMicrophone = useCallback(() => {
    const stopStreamTracks = (stream: MediaStream | null | undefined) => {
      stream?.getTracks?.().forEach((track) => {
        if (track.readyState === "live") {
          track.stop();
        }
      });
    };
    stopStreamTracks(mediaStreamRef.current);
    stopStreamTracks(recorderRef.current?.stream);
    mediaStreamRef.current = null;
    setMediaStream(null);
  }, []);

  const uploadDescription = useMemo(
    () => [
      `${t("dialogReport.pleaseUploadFile")}`,
      `<span class="fs-12">${t("dialogReport.thatCanBeUploadedAre")}</span>`,
      `<span class="fs-12">${t("dialogReport.fileFormats")}</span>`,
      `<span class="fs-12">${t("dialogReport.videosCanBeUploaded")}</span>`,
    ],
    [t],
  );

  const sortedTemplateItems = useMemo(() => {
    const raw = inspectionDetail?.inspectionItems;
    if (!raw || !Array.isArray(raw)) return [];
    return sortByPosition(raw) as TemplateInspectionItem[];
  }, [inspectionDetail]);

  const totalInspectionMediaCount = useMemo(
    () =>
      Object.values(itemDrafts).reduce(
        (sum, draft) => sum + (draft.mediaFiles?.length ?? 0),
        0,
      ),
    [itemDrafts],
  );

  useEffect(() => {
    setFlaggedInvalidItemIds((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set(prev);
      for (const id of prev) {
        const item = sortedTemplateItems.find(
          (it) => it.inspectionItemId === id,
        );
        if (item && !isInspectionItemInvalid(item, itemDrafts[id])) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [itemDrafts, sortedTemplateItems]);

  useEffect(() => {
    if (pendingExpansionQueue.length === 0) return;

    const stillPending = pendingExpansionQueue.filter((id) => {
      const item = sortedTemplateItems.find((it) => it.inspectionItemId === id);
      const draft = itemDrafts[id];
      return item && draft && needsCommentExpansionOnSend(item, draft);
    });

    if (stillPending.length === pendingExpansionQueue.length) return;

    setPendingExpansionQueue(stillPending);

    const newFront = stillPending[0];
    if (newFront && newFront !== pendingExpansionQueue[0]) {
      setItemDrafts((prev) => {
        const d = prev[newFront];
        if (!d || d.commentExpanded) return prev;
        return { ...prev, [newFront]: { ...d, commentExpanded: true } };
      });
    }
  }, [itemDrafts, pendingExpansionQueue, sortedTemplateItems]);

  const nonNumericTemplateItems = useMemo(
    () =>
      sortedTemplateItems.filter(
        (it) =>
          it.resultType !== ResultType.NUMERIC &&
          it.resultType !== ResultType.ODOMETER,
      ),
    [sortedTemplateItems],
  );

  const numericTemplateItems = useMemo(
    () =>
      sortedTemplateItems.filter(
        (it) =>
          it.resultType === ResultType.NUMERIC ||
          it.resultType === ResultType.ODOMETER,
      ),
    [sortedTemplateItems],
  );

  const iconResultTemplateItems = useMemo(
    () => sortedTemplateItems.filter(isIconResultItem),
    [sortedTemplateItems],
  );

  const hasAnyResultIconItems = iconResultTemplateItems.length > 0;

  const inspectionAtDate = useMemo(() => {
    if (!inspectionAt) return null;
    const parsed = new Date(inspectionAt);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [inspectionAt]);

  const isUpdateInspectionAtMissing = useMemo(
    () => formMode === "update" && !inspectionAtDate,
    [formMode, inspectionAtDate],
  );

  const isUpdateInspectionAtOutOfRange = useMemo(
    () =>
      formMode === "update" &&
      !!inspectionAtDate &&
      !isInspectionAtInValidRange(inspectionAtDate),
    [formMode, inspectionAtDate],
  );

  const isUpdateInspectionAtInvalid = useMemo(
    () => isUpdateInspectionAtMissing || isUpdateInspectionAtOutOfRange,
    [isUpdateInspectionAtMissing, isUpdateInspectionAtOutOfRange],
  );

  const inspectionAtShowError =
    (validationAttempted || inspectionAtTouched) && isUpdateInspectionAtInvalid;

  const inspectionAtHelperText = isUpdateInspectionAtMissing
    ? t("required")
    : isUpdateInspectionAtOutOfRange
      ? t("")
      : undefined;

  const latestItemsById = useMemo(() => {
    const map: Record<string, LatestInspectionItem> = {};
    for (const item of latestInspection?.inspectionItems ?? []) {
      if (item?.inspectionItemId) {
        map[item.inspectionItemId] = item;
      }
    }
    return map;
  }, [latestInspection]);

  useEffect(() => {
    if (open) {
      if (prefill) {
        const isUpdate =
          prefill.mode === "update" || !!prefill.existingInspection;
        setFormMode(isUpdate ? "update" : "create");
        setEditingInspectionId(prefill.inspectionId);
        setSelectDialogOpen(false);
        setDetailDialogOpen(true);

        const updateDrafts =
          isUpdate && prefill.existingInspection?.inspectionItems
            ? buildItemDraftsFromExistingInspection(
                prefill.existingInspection.inspectionItems,
              )
            : null;

        pendingInitialDraftsRef.current = updateDrafts;
        if (isUpdate && prefill.existingInspection) {
          existingInspectionRef.current = prefill.existingInspection;
          const baselines: Record<string, ItemDraftBaseline> = {};
          for (const item of prefill.existingInspection.inspectionItems ?? []) {
            baselines[item.inspectionItemId] = buildItemDraftBaseline(item);
          }
          originalItemBaselinesRef.current = baselines;
        } else {
          existingInspectionRef.current = null;
          originalItemBaselinesRef.current = {};
        }
        setInspectionDetail(
          prefill.existingInspection
            ? mapExistingInspectionToFormDetail(prefill.existingInspection)
            : prefill.inspectionDetail,
        );
        // History API stays the same for create + update: always fetch latest.
        setLatestInspection(null);
        setLatestInspectionPrefilled(false);
        setInspectionAt(prefill.inspectionAt);
        setInspectionAtTouched(false);
        setDetailError(null);
        setListError(null);
        setForms([]);
        setSelectedFormId(prefill.customInspectionFormId);
        // Apply update drafts immediately so create's "first item open" logic
        // cannot overwrite them when inspectionDetail effect runs.
        setItemDrafts(updateDrafts ?? {});
        setPreviousItemDialogOpen(false);
        setSelectedPreviousItemId(null);
        setValidationAttempted(false);
        setFlaggedInvalidItemIds(new Set());
        setPendingExpansionQueue([]);
      } else {
        setFormMode("create");
        setEditingInspectionId(undefined);
        pendingInitialDraftsRef.current = null;
        existingInspectionRef.current = null;
        originalItemBaselinesRef.current = {};
        setSelectDialogOpen(true);
        setDetailDialogOpen(false);
        setInspectionDetail(null);
        setDetailError(null);
        setListError(null);
        setForms([]);
        setSelectedFormId("");
        setItemDrafts({});
        setLatestInspection(null);
        setLatestInspectionPrefilled(false);
        setInspectionAt(undefined);
        setInspectionAtTouched(false);
        setPreviousItemDialogOpen(false);
        setSelectedPreviousItemId(null);
        setValidationAttempted(false);
        setFlaggedInvalidItemIds(new Set());
        setPendingExpansionQueue([]);
      }
    } else {
      setSelectDialogOpen(false);
      setDetailDialogOpen(false);
      setLatestInspectionPrefilled(false);
      setInspectionAt(undefined);
      setInspectionAtTouched(false);
      setFormMode("create");
      setEditingInspectionId(undefined);
      pendingInitialDraftsRef.current = null;
      existingInspectionRef.current = null;
      originalItemBaselinesRef.current = {};
      setValidationAttempted(false);
      setFlaggedInvalidItemIds(new Set());
      setPendingExpansionQueue([]);
    }
  }, [open, prefill]);

  useEffect(() => {
    if (!inspectionDetail?.inspectionItems) {
      if (!pendingInitialDraftsRef.current) {
        setItemDrafts({});
      }
      return;
    }

    if (pendingInitialDraftsRef.current) {
      setItemDrafts(pendingInitialDraftsRef.current);
      pendingInitialDraftsRef.current = null;
      setMediaPreviewNonce((n) => n + 1);
      return;
    }

    // Update mode already applied existing drafts above — do not reopen first item.
    if (formMode === "update") {
      return;
    }

    const items = sortByPosition(
      inspectionDetail.inspectionItems || [],
    ) as TemplateInspectionItem[];
    setItemDrafts(buildItemDraftsFromTemplate(items));
    setMediaPreviewNonce((n) => n + 1);
  }, [inspectionDetail, formMode]);

  useEffect(() => {
    if (
      !detailDialogOpen ||
      !groupId ||
      !machineId ||
      !inspectionDetail?.inspectionFormId ||
      latestInspectionPrefilled
    ) {
      return;
    }

    let cancelled = false;
    getLatestInspection(groupId, machineId, inspectionDetail.inspectionFormId)
      .then((data) => {
        if (!cancelled) {
          setLatestInspection((data as LatestInspectionData) || null);
        }
      })
      .catch((error) => {
        console.error("Failed to load latest inspection:", error);
        if (!cancelled) {
          setLatestInspection(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    detailDialogOpen,
    groupId,
    machineId,
    inspectionDetail?.inspectionFormId,
    latestInspectionPrefilled,
  ]);

  useEffect(() => {
    if (!selectDialogOpen || !groupId || !machineId) {
      return;
    }

    let cancelled = false;
    setListLoading(true);
    setListError(null);

    getInspectionForms(groupId, machineId)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        const publishedOnly = list.filter(
          (f) => f.currentStatus === CurrentStatusPostEnum.PUBLISHED,
        );
        setForms(publishedOnly);
        const first = publishedOnly[0];
        setSelectedFormId(first?.customInspectionFormId ?? "");
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setListError(t("Anunexpectederroroccurredwhileprocessingthefile"));
        }
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectDialogOpen, groupId, machineId, t]);

  const resetRecordingGlobals = useCallback(() => {
    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
    releaseMicrophone();
    setIsRecording(false);
    setIsPaused(false);
    setRecorder(null);
    recorderRef.current = null;
    setMediaAvailable(true);
    setRecordingForItemId(null);
    recordingTargetItemIdRef.current = null;
    recordingStartTimeRef.current = null;
    stoppedRef.current = false;
  }, [releaseMicrophone]);

  const stopRecording = useCallback(() => {
    const activeRecorder = recorderRef.current;
    if (!activeRecorder) {
      releaseMicrophone();
      return;
    }

    stoppedRef.current = true;

    setIsRecording(false);
    setIsPaused(false);
    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);

    const targetId = recordingTargetItemIdRef.current;

    releaseMicrophone();

    activeRecorder.stopRecording(() => {
      window.setTimeout(() => {
        const raw = activeRecorder.getBlob() as Blob | undefined;
        const skip = skipNextRecordingBlobRef.current;
        skipNextRecordingBlobRef.current = false;

        if (!skip && raw && raw.size > 44 && targetId) {
          const playable = cloneBlobForPlayback(raw);
          setItemDrafts((prev) => ({
            ...prev,
            [targetId]: { ...prev[targetId], recordedBlob: playable },
          }));
        }

        setRecorder(null);
        recorderRef.current = null;
        recordingStartTimeRef.current = null;
        setRecordingForItemId(null);
        recordingTargetItemIdRef.current = null;
      }, 300);
    });
  }, [releaseMicrophone]);

  const startRecordingForItem = useCallback(
    async (itemId: string) => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasMic = devices.some((device) => device.kind === "audioinput");

        if (!hasMic) {
          setOpenFormNoMic(true);
          return;
        }

        releaseMicrophone();

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        mediaStreamRef.current = stream;
        setMediaStream(stream);

        recordingTargetItemIdRef.current = itemId;
        setRecordingForItemId(itemId);

        setItemDrafts((prev) => ({
          ...prev,
          [itemId]: { ...prev[itemId], recordedBlob: null },
        }));

        setIsPaused(false);
        stoppedRef.current = false;

        const newRecorder = new RecordRTC(stream, {
          type: "audio",
          mimeType: "audio/webm",
          recorderType: RecordRTC.StereoAudioRecorder,
          numberOfAudioChannels: 1,
          desiredSampRate: 16000,
          audioBitsPerSecond: 64000,
        });

        recorderRef.current = newRecorder;
        recordingStartTimeRef.current = Date.now();
        newRecorder.startRecording();
        setRecorder(newRecorder);
        setIsRecording(true);

        const timeout = setTimeout(() => {
          stopRecording();
        }, 59200);

        recordingTimeoutRef.current = timeout;
      } catch (err) {
        console.warn("Microphone access denied", err);
        releaseMicrophone();
        setMediaAvailable(false);
        setRecordingForItemId(null);
        recordingTargetItemIdRef.current = null;
      }
    },
    [releaseMicrophone, stopRecording],
  );

  const handleDeleteRecordingConfirmed = useCallback(() => {
    const itemId = pendingDeleteRecordingItemIdRef.current;

    if (isRecording || isPaused) {
      if (recordingTargetItemIdRef.current === itemId) {
        skipNextRecordingBlobRef.current = true;
      }
      stopRecording();
    }

    releaseMicrophone();

    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);

    if (itemId) {
      setItemDrafts((prev) => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          recordedBlob: null,
          existingRecordingUrl: null,
        },
      }));
    }

    setIsPaused(false);
    setIsRecording(false);
    setRecorder(null);
    recorderRef.current = null;
    setMediaAvailable(true);
    setRecordingForItemId(null);
    recordingTargetItemIdRef.current = null;
    setOpenFormDeleteRecording(false);
    pendingDeleteRecordingItemIdRef.current = null;
    recordingStartTimeRef.current = null;
  }, [isRecording, isPaused, stopRecording, releaseMicrophone]);

  const requestDeleteRecording = useCallback((itemId: string) => {
    pendingDeleteRecordingItemIdRef.current = itemId;
    setOpenFormDeleteRecording(true);
  }, []);

  const patchDraft = useCallback(
    (itemId: string, patch: Partial<ItemDraft>) => {
      setItemDrafts((prev) => ({
        ...prev,
        [itemId]: { ...prev[itemId], ...patch },
      }));
    },
    [],
  );

  const handleResultIconPick = useCallback((itemId: string, value: string) => {
    setValidationAttempted(true);
    const expandForAbnormal = value === ItemResultType.ANOMARY;
    if (value) {
      setFlaggedInvalidItemIds((prev) => {
        if (!prev.has(itemId)) return prev;
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
    setItemDrafts((prev) => {
      const prevDraft = prev[itemId];
      const deselectingAbnormal =
        prevDraft?.resultValue === ItemResultType.ANOMARY && !value;
      return {
        ...prev,
        [itemId]: {
          ...prevDraft,
          resultValue: value,
          commentExpanded: expandForAbnormal
            ? true
            : deselectingAbnormal
              ? false
              : prevDraft?.commentExpanded,
        },
      };
    });
    if (expandForAbnormal) {
      requestAnimationFrame(() => {
        itemRefs.current[itemId]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    } else if (value) {
      requestAnimationFrame(() => {
        scrollNearestScrollableAncestor(itemRefs.current[itemId]);
      });
    }
  }, []);

  const handleToggleSelectAllNormal = useCallback(() => {
    setItemDrafts((prev) => {
      const allOk =
        iconResultTemplateItems.length > 0 &&
        iconResultTemplateItems.every(
          (it) => prev[it.inspectionItemId]?.resultValue === ItemResultType.OK,
        );
      if (!allOk) {
        setFlaggedInvalidItemIds(new Set());
        setPendingExpansionQueue([]);
      }
      return applyOkSelectionToDrafts(prev, iconResultTemplateItems, !allOk);
    });
  }, [iconResultTemplateItems]);

  const allIconItemsAreNormal = useMemo(() => {
    if (iconResultTemplateItems.length === 0) return false;
    return iconResultTemplateItems.every(
      (it) =>
        itemDrafts[it.inspectionItemId]?.resultValue === ItemResultType.OK,
    );
  }, [iconResultTemplateItems, itemDrafts]);

  const handleToggleCommentSection = useCallback((itemId: string) => {
    setItemDrafts((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        commentExpanded: !prev[itemId].commentExpanded,
      },
    }));
  }, []);

  const handleCloseSelect = useCallback(() => {
    setSelectDialogOpen(false);
    onClose();
  }, [onClose]);

  const handleSelectOk = useCallback(async () => {
    if (!groupId || !machineId || !selectedFormId) return;

    setDetailLoading(true);
    setDetailError(null);

    try {
      const detail = (await getCustomInspectionFormTemplates(
        groupId,
        machineId,
        selectedFormId,
      )) as InspectionFormDetailResponse;

      setInspectionDetail({
        ...detail,
        inspectionItems: sortByPosition(detail.inspectionItems || []),
      });
      setSelectDialogOpen(false);
      setDetailDialogOpen(true);
    } catch (err) {
      console.error(err);
      setDetailError(t("Anunexpectederroroccurredwhileprocessingthefile"));
    } finally {
      setDetailLoading(false);
    }
  }, [groupId, machineId, selectedFormId, t]);

  const handleCloseDetail = useCallback(() => {
    if (isRecording || recorder) {
      stopRecording();
    }
    resetRecordingGlobals();
    setOpenCancelConfirm(false);
    setSignatureDialogOpen(false);
    setValidationAttempted(false);
    setFlaggedInvalidItemIds(new Set());
    setPendingExpansionQueue([]);
    setDetailDialogOpen(false);
    setInspectionDetail(null);
    setItemDrafts({});
    setLatestInspection(null);
    setLatestInspectionPrefilled(false);
    setInspectionAt(undefined);
    setInspectionAtTouched(false);
    setFormMode("create");
    setEditingInspectionId(undefined);
    pendingInitialDraftsRef.current = null;
    setPreviousItemDialogOpen(false);
    setSelectedPreviousItemId(null);
    setViewerOpen(false);
    setSelectedImage("");
    onClose();
  }, [isRecording, recorder, stopRecording, resetRecordingGlobals, onClose]);

  const handleRequestCloseDetail = useCallback(() => {
    setOpenCancelConfirm(true);
  }, []);

  const handleSignatureClose = useCallback(() => {
    setSignatureDialogOpen(false);
  }, []);

  const handleSignatureSend = useCallback(
    async (
      signatureDataUrl: string | null,
      options?: { submitAlreadyStarted?: boolean },
    ) => {
      if (!groupId || !machineId || !inspectionDetail?.inspectionFormId) {
        return;
      }

      if (isRecording || recorder) {
        stopRecording();
      }

      if (!options?.submitAlreadyStarted) {
        if (submitInFlightRef.current) return;
        submitInFlightRef.current = true;
        setIsSubmittingInspection(true);
      }

      dispatch(setIsLoading(true));

      try {
        if (formMode === "update") {
          if (!editingInspectionId) {
            return;
          }

          const { successMessage } = await submitUpdateInspection({
            groupId,
            machineId,
            inspectionId: editingInspectionId,
            inspectionFormId: inspectionDetail.inspectionFormId,
            items: sortedTemplateItems,
            itemDrafts,
            baselines: originalItemBaselinesRef.current,
            originalItems: existingInspectionRef.current?.inspectionItems ?? [],
            inspectionAt,
            signatureDataUrl,
          });

          dispatch(
            setShowAlert(successMessage || t("updateinspectionsuccesstime")),
          );
          dispatch(setIsUpdatedMachineReportResponses(`${Date.now()}`));
          setSignatureDialogOpen(false);
          onSuccess?.();
          handleCloseDetail();
          return;
        }

        const { successMessage } = await submitCreateInspection({
          groupId,
          machineId,
          inspectionFormId: inspectionDetail.inspectionFormId,
          items: sortedTemplateItems,
          itemDrafts,
          signatureDataUrl,
          inspectionAt,
        });

        dispatch(
          setShowAlert(successMessage || t("createInspection.submitSuccess")),
        );
        dispatch(setIsUpdatedMachineReportResponses(`${Date.now()}`));
        setSignatureDialogOpen(false);
        onSuccess?.();
        handleCloseDetail();
      } catch (error) {
        console.error("Failed to create inspection:", error);
        const message =
          error instanceof AxiosError
            ? error.response?.data?.error?.message ||
              error.response?.data?.meta?.errorMessage
            : null;
        dispatch(
          setShowAlert(
            message || t("Anunexpectederroroccurredwhileprocessingthefile"),
          ),
        );
      } finally {
        submitInFlightRef.current = false;
        setIsSubmittingInspection(false);
        dispatch(setIsLoading(false));
      }
    },
    [
      groupId,
      machineId,
      inspectionDetail?.inspectionFormId,
      sortedTemplateItems,
      itemDrafts,
      inspectionAt,
      formMode,
      editingInspectionId,
      isRecording,
      recorder,
      stopRecording,
      dispatch,
      t,
      onSuccess,
      handleCloseDetail,
    ],
  );

  const handleOpenSignatureDialog = useCallback(async () => {
    if (!groupId || !machineId) return;
    if (submitInFlightRef.current) return;

    setValidationAttempted(true);
    setFlaggedInvalidItemIds(
      findAllInvalidInspectionItemIds(sortedTemplateItems, itemDrafts),
    );

    const expansionQueue = findItemsNeedingCommentExpansion(
      sortedTemplateItems,
      itemDrafts,
    );
    setPendingExpansionQueue(expansionQueue);
    const firstToExpand = expansionQueue[0];
    if (firstToExpand) {
      setItemDrafts((prev) => {
        const d = prev[firstToExpand];
        if (!d || d.commentExpanded) return prev;
        return { ...prev, [firstToExpand]: { ...d, commentExpanded: true } };
      });
    }

    if (hasInspectionFormValidationErrors(sortedTemplateItems, itemDrafts)) {
      const invalidItemId = findFirstInvalidInspectionItemId(
        sortedTemplateItems,
        itemDrafts,
      );
      if (invalidItemId) {
        requestAnimationFrame(() => {
          itemRefs.current[invalidItemId]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      }
      return;
    }

    if (isUpdateInspectionAtInvalid) {
      requestAnimationFrame(() => {
        inspectionAtFieldRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
      return;
    }

    submitInFlightRef.current = true;
    setIsSubmittingInspection(true);

    try {
      if (isRecording || recorder) {
        stopRecording();
      }

      const isSignatureRequired = await getGroupIsSignatureRequired(groupId);
      if (isSignatureRequired) {
        submitInFlightRef.current = false;
        setIsSubmittingInspection(false);
        setSignatureDialogOpen(true);
        return;
      }

      await handleSignatureSend(null, { submitAlreadyStarted: true });
    } catch (error) {
      console.error("Failed to open signature dialog:", error);
      submitInFlightRef.current = false;
      setIsSubmittingInspection(false);
    }
  }, [
    groupId,
    handleSignatureSend,
    isRecording,
    itemDrafts,
    machineId,
    recorder,
    sortedTemplateItems,
    stopRecording,
    isUpdateInspectionAtInvalid,
  ]);

  const showRequiredTitleBadge = useMemo(
    () =>
      validationAttempted &&
      hasInspectionFormValidationErrors(sortedTemplateItems, itemDrafts),
    [validationAttempted, sortedTemplateItems, itemDrafts],
  );

  const isSendHardDisabled = useMemo(() => {
    if (isSubmittingInspection) return true;
    if (isRecording || isPaused) return true;
    if (sortedTemplateItems.length === 0) return true;
    if (totalInspectionMediaCount > INSPECTION_MEDIA_TOTAL_LIMIT) return true;

    return false;
  }, [
    sortedTemplateItems.length,
    isRecording,
    isPaused,
    totalInspectionMediaCount,
    isSubmittingInspection,
  ]);

  const canConfirmSelect =
    !!selectedFormId &&
    !!groupId &&
    !!machineId &&
    !listLoading &&
    forms.length > 0;

  const openTemplateMediaPreview = useCallback(
    (media: TemplateInspectionItemMedia) => {
      const url = getTemplateMediaPreviewUrl(media);
      if (!url) return;
      if (isTemplateMediaVideo(media)) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      openImageInViewer(url, setSelectedImage, setViewerOpen);
    },
    [],
  );

  const handleUploadPreview = useCallback((url: string | string[]) => {
    const u = Array.isArray(url) ? url[0] : url;
    openImageInViewer(u, setSelectedImage, setViewerOpen);
  }, []);

  const setItemRef = useCallback(
    (itemId: string, el: HTMLDivElement | null) => {
      itemRefs.current[itemId] = el;
    },
    [],
  );

  const handleMediaFilesChange = useCallback(
    (itemId: string, files: File[]) => {
      setItemDrafts((prev) => {
        const cappedFiles = capMediaFilesForItem(prev, itemId, files);
        return buildMediaDraftUpdate(prev, itemId, cappedFiles);
      });
    },
    [],
  );

  const handleFileCountChange = useCallback(
    (itemId: string, count: number | ((prev: number) => number)) => {
      setItemDrafts((prev) => {
        const d = prev[itemId];
        if (!d) return prev;
        const nextCount =
          typeof count === "function" ? count(d.fileCount) : count;
        return {
          ...prev,
          [itemId]: { ...d, fileCount: nextCount },
        };
      });
    },
    [],
  );

  const handleUploadErrorChange = useCallback(
    (itemId: string, hasError: boolean) => {
      setItemDrafts((prev) => {
        const d = prev[itemId];
        if (!d) return prev;
        return {
          ...prev,
          [itemId]: { ...d, hasUploadError: hasError },
        };
      });
    },
    [],
  );

  const recordingStartTimeMs = recordingStartTimeRef.current;

  const handleOpenPreviousResult = useCallback((itemId: string) => {
    setSelectedPreviousItemId(itemId);
    setPreviousItemDialogOpen(true);
  }, []);

  const handleClosePreviousResult = useCallback(() => {
    setPreviousItemDialogOpen(false);
    setSelectedPreviousItemId(null);
  }, []);

  const selectedPreviousItem = selectedPreviousItemId
    ? (latestItemsById[selectedPreviousItemId] ?? null)
    : null;

  return (
    <>
      <DataDialog
        open={selectDialogOpen}
        onClose={handleCloseSelect}
        title={t("customInspectionForms")}
        fullWidth
        dialogContentSx={{ overflow: "visible" }}
        dialogActions={
          <>
            <Button
              className="btn btn-no-border"
              style={{ width: "auto", marginRight: "0px" }}
              onClick={handleCloseSelect}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              className="btn btn-primary"
              style={{ width: "auto" }}
              onClick={handleSelectOk}
              disabled={!canConfirmSelect || detailLoading}
            >
              {t("ok")}
            </Button>
          </>
        }
      >
        <Box sx={{ pt: 2, overflow: "visible" }}>
          {!listLoading && listError && (
            <Typography color="error">{listError}</Typography>
          )}
          {!listLoading && !listError && forms.length === 0 && (
            <Typography>{t("noInspectionForms")}</Typography>
          )}
          {!listLoading && !listError && forms.length > 0 && (
            <FormControl fullWidth sx={{ mt: 0.5 }}>
              <InputLabel id="create-inspection-form-select-label">
                {t("inspectionTable.inspectionName")}
              </InputLabel>
              <Select
                labelId="create-inspection-form-select-label"
                id="create-inspection-form-select"
                label={t("inspectionTable.inspectionName")}
                value={selectedFormId}
                onChange={(e) => setSelectedFormId(e.target.value as string)}
              >
                {forms.map((f) => (
                  <MenuItem
                    key={f.customInspectionFormId}
                    value={f.customInspectionFormId}
                  >
                    {f.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {detailError && (
            <Typography color="error" sx={{ mt: 2 }}>
              {detailError}
            </Typography>
          )}
        </Box>
      </DataDialog>

      <DataDialog
        open={detailDialogOpen}
        onClose={handleRequestCloseDetail}
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{inspectionDetail?.name ?? ""}</span>
            <span style={{ color: "grey", fontSize: "12px", fontWeight: 500 }}>
              *{t("required")}
            </span>
          </div>
        }
        fullWidth
        disableEnforceFocus
        className="dialog-container"
        dialogContentSx={{
          overflow: "auto",
          maxHeight: "min(70vh, 720px)",
          touchAction: "auto",
        }}
        dialogActions={
          <>
            <Button
              className="btn btn-no-border"
              style={{ width: "auto", marginRight: "0px" }}
              onClick={handleRequestCloseDetail}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              className="btn btn-primary"
              style={{ width: "auto" }}
              onClick={handleOpenSignatureDialog}
              disabled={isSendHardDisabled}
            >
              {formMode === "update"
                ? t("formAction.update")
                : t("createInspection.send")}
            </Button>
          </>
        }
      >
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 2 }}
          onClick={markValidationHints}
        >
          {nonNumericTemplateItems.length > 0 && (
            <Box
              sx={{
                borderRadius: 1,
                bgcolor: "#fff",
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {nonNumericTemplateItems.map((item, itemIndex) => {
                const draft = itemDrafts[item.inspectionItemId];
                if (!draft) return null;

                return (
                  <InspectionNonNumericItemRow
                    key={item.inspectionItemId}
                    item={item}
                    itemIndex={itemIndex}
                    draft={draft}
                    validationAttempted={validationAttempted}
                    mediaAvailable={mediaAvailable}
                    isRecording={isRecording}
                    isPaused={isPaused}
                    recordingForItemId={recordingForItemId}
                    recordingStartTimeMs={recordingStartTimeMs}
                    totalInspectionMediaCount={totalInspectionMediaCount}
                    mediaPreviewNonce={mediaPreviewNonce}
                    uploadDescription={uploadDescription}
                    onPatchDraft={patchDraft}
                    onToggleCommentSection={handleToggleCommentSection}
                    onResultIconPick={handleResultIconPick}
                    onStartRecording={startRecordingForItem}
                    onStopRecording={stopRecording}
                    onRequestDeleteRecording={requestDeleteRecording}
                    onPreviewTemplateMedia={openTemplateMediaPreview}
                    onPreviewUploadMedia={handleUploadPreview}
                    onMediaFilesChange={handleMediaFilesChange}
                    onFileCountChange={handleFileCountChange}
                    onUploadErrorChange={handleUploadErrorChange}
                    setItemRef={setItemRef}
                    hasError={
                      flaggedInvalidItemIds.has(item.inspectionItemId) &&
                      isInspectionItemInvalid(item, draft)
                    }
                    hasPreviousResult={hasLatestItemPreviousData(
                      latestItemsById[item.inspectionItemId],
                      item,
                    )}
                    isAbnormalPreviousResult={isLatestItemPreviousAbnormal(
                      latestItemsById[item.inspectionItemId],
                    )}
                    onOpenPreviousResult={handleOpenPreviousResult}
                  />
                );
              })}
            </Box>
          )}

          {hasAnyResultIconItems && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Button
                className="btn btn-secondary"
                startIcon={<CheckIcon className="icon-add" />}
                onClick={handleToggleSelectAllNormal}
                sx={{ minWidth: 220 }}
              >
                {allIconItemsAreNormal
                  ? t("formAction.deselectAll")
                  : t("selectAll")}
              </Button>
            </Box>
          )}

          {numericTemplateItems.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: 2,
              }}
            >
              {numericTemplateItems.map((item) => {
                const draft = itemDrafts[item.inspectionItemId];
                if (!draft) return null;

                return (
                  <InspectionNumericItemRow
                    key={item.inspectionItemId}
                    item={item}
                    draft={draft}
                    validationAttempted={validationAttempted}
                    onPatchDraft={patchDraft}
                    setItemRef={setItemRef}
                    hasPreviousResult={hasLatestItemPreviousData(
                      latestItemsById[item.inspectionItemId],
                      item,
                    )}
                    isAbnormalPreviousResult={isLatestItemPreviousAbnormal(
                      latestItemsById[item.inspectionItemId],
                    )}
                    onOpenPreviousResult={handleOpenPreviousResult}
                  />
                );
              })}

              {formMode === "update" && (
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={enUS}
                >
                  <Box ref={inspectionAtFieldRef}>
                    <DateTimePicker
                      label={t("tableMachineDetail.date")}
                      className="input-field"
                      format={
                        userTimeFormat?.includes("HH") ||
                        userTimeFormat?.includes("mm")
                          ? userTimeFormat
                          : `${userTimeFormat || "yyyy/MM/dd"} HH:mm`
                      }
                      value={inspectionAtDate ?? undefined}
                      minDateTime={INSPECTION_AT_MIN_DATE}
                      maxDateTime={new Date()}
                      onChange={(value) => {
                        if (!value || Number.isNaN(value.getTime())) {
                          setInspectionAt(undefined);
                          return;
                        }
                        setInspectionAt(value.toISOString());
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: inspectionAtShowError,
                          helperText: inspectionAtHelperText,
                          onBlur: () => setInspectionAtTouched(true),
                        },
                      }}
                    />
                  </Box>
                </LocalizationProvider>
              )}
            </Box>
          )}
        </Box>
      </DataDialog>

      <SignatureDialog
        open={signatureDialogOpen}
        onClose={handleSignatureClose}
        onSend={handleSignatureSend}
        isSubmitting={isSubmittingInspection}
      />

      <PreviousInspectionItemDialog
        open={previousItemDialogOpen}
        onClose={handleClosePreviousResult}
        item={selectedPreviousItem}
        inspection={latestInspection}
      />

      <DataDialog
        open={openCancelConfirm}
        setOpen={setOpenCancelConfirm}
        disableBackdropClick={true}
      >
        <Typography variant="body1" sx={{ mb: 3, fontWeight: 700 }}>
          {t("cancelAddingGroup")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => setOpenCancelConfirm(false)}
          >
            {t("dialogReport.cancel")}
          </Button>
          <Button className="btn btn-primary" onClick={handleCloseDetail}>
            {t("ok")}
          </Button>
        </Box>
      </DataDialog>

      <DataDialog
        title={t("errNetwork.title")}
        open={openFormNoMic}
        setOpen={setOpenFormNoMic}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("noMic")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFormNoMic(false)}
              className="btn btn-primary ml-8"
            >
              {t("ok")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        title={t("deleteRecording")}
        open={openFormDeleteRecording}
        setOpen={setOpenFormDeleteRecording}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("inspectionDelete.content")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => {
                setOpenFormDeleteRecording(false);
                pendingDeleteRecordingItemIdRef.current = null;
              }}
              className="btn btn-no-border"
              style={{ marginTop: "4px" }}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              onClick={handleDeleteRecordingConfirmed}
              className="btn-delete"
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      {typeof document !== "undefined" &&
        viewerOpen &&
        selectedImage &&
        createPortal(
          <CustomImageViewer
            imageUrl={selectedImage}
            onClose={() => {
              setViewerOpen(false);
              setSelectedImage("");
            }}
          />,
          document.body,
        )}
    </>
  );
}
